import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';

// Mapping from the provided source
const IntensityUnitMap = {
  CarbonIntensity: 'kg CO₂e',
  WaterIntensity: 'kL',
  WasteIntensity: 'Kg',
  HazardousWasteIntensity: 'Kg',
  NonHazardousWasteIntensity: 'Kg',
  BioMedicalWasteIntensity: 'Kg',
  FuelIntensity: 'GJ',
  RenewableEnergyIntensity: 'kWh',
  ElectricityIntensity: 'kWh',
};

const IntensitySummaryCard = ({
  rawData,
  selectedIntensityType,
  selectedPeriodsValue,
  selectedLocations,
  targetKpi,
}) => {
  const processedData = useMemo(() => {
    if (!rawData.length || !selectedIntensityType || !selectedPeriodsValue.length)
      return null;

    const validLocIds = selectedLocations.map(id => id.toString());
    const validMonths = selectedPeriodsValue.map(p => p.fromDate.substring(0, 7));

    const numeratorUnit = IntensityUnitMap[selectedIntensityType.value] || '';
    let denominatorUnit = '';

    if (targetKpi.includes('Cr')) denominatorUnit = 'Cr';
    else if (targetKpi.includes('sq. ft')) denominatorUnit = 'sq. ft';
    else if (targetKpi.includes('Served')) denominatorUnit = 'Patient';
    else if (targetKpi.includes('Beds')) denominatorUnit = 'Bed';

    const finalUnit =
      numeratorUnit && denominatorUnit
        ? `${numeratorUnit}/${denominatorUnit}`
        : numeratorUnit;

    const isSumRule =
      targetKpi.includes('Revenue') || targetKpi.includes('Patient Served');

    if (isSumRule) {
      let answerSum = 0;
      let metricValueSum = 0;

      rawData.forEach(item => {
        const month = item.fromDate.substring(0, 7);
        if (
          item.type === selectedIntensityType.value &&
          validLocIds.includes(item.sourceId?.toString()) &&
          validMonths.includes(month)
        ) {
          const ansData = item.intensityQuestionAnswers?.find(
            a => a.title === targetKpi
          );
          answerSum += parseFloat(ansData?.answer || 0);
          metricValueSum += parseFloat(ansData?.metricValue || 0);
        }
      });

      return {
        value: answerSum > 0 ? metricValueSum / answerSum : 0,
        unit: finalUnit,
      };
    }

    // non-sum rule (FIXED)
    let metricValueSum = 0;

    // location → period → answer
    const locationPeriodMap = {};

    rawData.forEach(item => {
      const month = item.fromDate.substring(0, 7);

      if (
        item.type === selectedIntensityType.value &&
        validLocIds.includes(item.sourceId?.toString()) &&
        validMonths.includes(month)
      ) {
        const ansData = item.intensityQuestionAnswers?.find(
          a => a.title === targetKpi
        );

        const answer = parseFloat(ansData?.answer || 0);
        const metricValue = parseFloat(ansData?.metricValue || 0);

        metricValueSum += metricValue;

        if (!locationPeriodMap[item.sourceId]) {
          locationPeriodMap[item.sourceId] = {};
        }

        // ⬇️ key point: one entry per PERIOD per LOCATION
        if (!locationPeriodMap[item.sourceId][month]) {
          locationPeriodMap[item.sourceId][month] = answer;
        } else {
          locationPeriodMap[item.sourceId][month] += answer;
        }
      }
    });

    // compute denominator
    const denominator = Object.values(locationPeriodMap).reduce(
      (locationSum, periodMap) => {
        const periodAnswers = Object.values(periodMap);
        if (!periodAnswers.length) return locationSum;

        const avgForLocation =
          periodAnswers.reduce((a, b) => a + b, 0) / periodAnswers.length;

        return locationSum + avgForLocation;
      },
      0
    );

    return {
      value: denominator > 0 ? metricValueSum / denominator : 0,
      unit: finalUnit,
    };

  }, [
    rawData,
    selectedIntensityType,
    selectedPeriodsValue,
    selectedLocations,
    targetKpi,
  ]);

  return (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Header className="bg-white border-0 pt-3">
        <h6 className="mb-0 fw-bold text-secondary">{targetKpi}</h6>
      </Card.Header>

      <Card.Body className="d-flex flex-column justify-content-center align-items-center">
        {processedData ? (
          <>
            <div className="display-5 fw-bold text-dark">
              {Number(processedData.value).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="text-muted small mt-1">
              {processedData.unit}
            </div>
          </>
        ) : (
          <div className="text-muted small">No data available</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default IntensitySummaryCard;
